import React, {
  useState, useCallback, useEffect, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { DeliveryRefinements } from './DeliveryRefinements';
import { InNearbyStoreContainer } from './InNearbyStore/InNearbyStoreContainer';
import { FulfillmentTypeSelector } from './FulfillmentTypeSelector';
import { useSearchModelData } from '../../hooks/useSearchModelData';
import {
  BROWSE_ALL_REFINEMENT,
  GET_IT_FAST_DIMENSION_LABEL,
  FULFILLMENT_UI_LABELS,
  FULFILLMENT_DIMENSIONS_LABELS,
  DELIVERY_REFINEMENTS
} from '../../utils/constants';
import {
  getFulfillmentTileLabel,
  findDimensionByLabels,
  getAppliedDeliveryDimension,
  getRefinementCount,
  createFulfillmentDimension,
  getFulfillmentDimension,
  getAppliedDimensionLabel
} from '../../utils/fulfillment-helpers';

const { func, string } = PropTypes;

const SelectedFulfillmentContent = ({
  selectedFulfillment,
  onDimensionChange,
  onRefinementChange
}) => {
  switch (selectedFulfillment) {
  case FULFILLMENT_UI_LABELS.IN_STORE:
    return <InNearbyStoreContainer />;
  case FULFILLMENT_UI_LABELS.DELIVERY:
  case FULFILLMENT_UI_LABELS.FREE_DELIVERY:
    return (
      <DeliveryRefinements
        className="sm:sui-flex-row sm:sui-justify-start md:sui-flex-col xl:sui-flex-row sm:sui-pt-4 sui-pt-3 xl:sui-pt-4"
        onDimensionChange={onDimensionChange}
        onRefinementChange={onRefinementChange}
      />
    );
  default:
    return null;
  }
};

SelectedFulfillmentContent.propTypes = {
  selectedFulfillment: string.isRequired,
  onDimensionChange: func.isRequired,
  onRefinementChange: func.isRequired
};

const FulfillmentOptions = ({
  onFulfillmentChange
}) => {
  const { data } = useSearchModelData();
  const { dimensions = [], appliedDimensions = [], searchReport } = data?.searchModel || {};
  const fulfillmentDimensions = useMemo(() => createFulfillmentDimension(dimensions), [dimensions]);
  const appliedFulfillmentDimension = useMemo(() => getFulfillmentDimension(appliedDimensions), [appliedDimensions]);
  const appliedDimensionLabel = useMemo(() => getAppliedDimensionLabel(appliedFulfillmentDimension), [appliedFulfillmentDimension]);
  // do we need this totalProducts value?
  const { totalProducts = 0 } = searchReport || {};
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false);
  const previousDimensionsLength = useRef(0);
  const previousShouldRenderResult = useRef(false);
  const decisionToRenderCompleted = useRef(false);
  const [selectedTile, setSelectedTile] = useState(
    getFulfillmentTileLabel(appliedDimensionLabel) ?? BROWSE_ALL_REFINEMENT.label
  );

  // Check if we should render the supertoggle
  const shouldRender = useCallback(() => {
    if (!dimensions || dimensions.length === 0) {
      return false;
    }

    const getItFastDim = findDimensionByLabels(dimensions, [FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST]);
    const deliveryDim = findDimensionByLabels(dimensions, [FULFILLMENT_DIMENSIONS_LABELS.DELIVERY]);

    const inStoreCount = getRefinementCount(getItFastDim, 'Pick Up Today');
    const deliveryCount = getRefinementCount(deliveryDim, 'Anytime');

    return !!deliveryDim && (inStoreCount > 0 || deliveryCount > 0);
  }, [dimensions]);

  const getAnalyticsForFulfillment = (value, speed) => {
    return {
      filterType: 'primary',
      type: GET_IT_FAST_DIMENSION_LABEL,
      value: value === 'browseall' ? FULFILLMENT_UI_LABELS.BROWSE_ALL : value,
      ...(value === 'instore' && { nearbyStore: 'y' }),
      ...(value === 'delivery' && { speed: speed ?? DELIVERY_REFINEMENTS.ANYTIME, freeDelivery: 'n' }),
      ...(value === 'freedelivery' && { value: FULFILLMENT_UI_LABELS.DELIVERY, speed: speed ?? DELIVERY_REFINEMENTS.ANYTIME, freeDelivery: 'y' })
    };
  };

  const handleFulfillmentChange = useCallback(({ refinement, dimension }) => {
    const isDelivery = [FULFILLMENT_UI_LABELS.DELIVERY, FULFILLMENT_UI_LABELS.FREE_DELIVERY].includes(dimension?.label);
    const isBrowseAll = refinement?.label === FULFILLMENT_UI_LABELS.BROWSE_ALL;
    let selectedFulfillment;

    if (isDelivery) {
      selectedFulfillment = dimension?.label;
    } else {
      selectedFulfillment = refinement?.label;
    }

    document.body.classList.add('sui-opacity-50');

    if (isBrowseAll) {
      let fulfillmentDimension = appliedDimensions?.find(
        (appliedDim) => Object.values(FULFILLMENT_DIMENSIONS_LABELS).includes(appliedDim.label.toUpperCase())
      );
      // eslint-disable-next-line no-param-reassign
      refinement.url = fulfillmentDimension?.refinements[0]?.url;
    }

    setTimeout(() => {
      const analytics = getAnalyticsForFulfillment(selectedFulfillment.replace(/\s+/g, '').toLowerCase(), refinement.label);
      const refinementWithAnalytics = {
        ...refinement,
        dimensionName: GET_IT_FAST_DIMENSION_LABEL,
        analytics,
        redirectUrl: null,
        selected: null
      };

      onFulfillmentChange({ refinement: refinementWithAnalytics });
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  }, [
    appliedDimensions,
    onFulfillmentChange
  ]);

  const handleTileChange = useCallback(({ refinement, dimension }) => {
    const isDelivery = [FULFILLMENT_UI_LABELS.DELIVERY, FULFILLMENT_UI_LABELS.FREE_DELIVERY].includes(dimension?.label);

    if (!refinement?.label
      || (isDelivery ? selectedTile === FULFILLMENT_UI_LABELS.DELIVERY
        // For non-delivery dimensions, just compare the selected tile with the refinement label
        : refinement.label === selectedTile)) {
      return;
    }
    setSelectedTile(isDelivery ? FULFILLMENT_UI_LABELS.DELIVERY : refinement?.label);
    // Avoid triggering fulfillment change to Delivery if the applied dimension is Delivery or Free Delivery
    if (!(isDelivery && selectedTile === FULFILLMENT_UI_LABELS.DELIVERY)) {
      handleFulfillmentChange({ refinement, dimension });
    }
  }, [
    handleFulfillmentChange,
    selectedTile
  ]);

  const handleRefinementChange = useCallback((refinementAndDimension) => {
    const refinement = refinementAndDimension.refinement;
    const analytics = getAnalyticsForFulfillment(refinementAndDimension.dimension.label.replace(/\s+/g, '').toLowerCase(), refinement.label);
    const refinementWithAnalytics = {
      ...refinement,
      dimensionName: GET_IT_FAST_DIMENSION_LABEL,
      analytics,
      redirectUrl: null,
      selected: null
    };
    onFulfillmentChange({
      refinement: refinementWithAnalytics
    });
  }, [onFulfillmentChange]);

  useEffect(() => {
    const shouldRenderComponent = shouldRender();
    setShow(shouldRenderComponent);
    decisionToRenderCompleted.current = true;
    previousShouldRenderResult.current = shouldRenderComponent;

    if (dimensions.length !== previousDimensionsLength.current && totalProducts > 0) {
      // Set loading to true when dimensions change
      setIsLoading(true);
      // Reset the decision flag
      decisionToRenderCompleted.current = false;
    } else if (dimensions.length === 0) {
      // No dimensions, no loading
      setIsLoading(false);
    }

    previousDimensionsLength.current = dimensions.length;
  }, [shouldRender, totalProducts]);

  useEffect(() => {
    // Set loading to false on show changes or when shouldRender has completed its decision
    // after a non-initial render, like dimensions updates or refinement changes
    if (previousShouldRenderResult.current !== show
      || (previousShouldRenderResult.current === show && isLoading)
      || (decisionToRenderCompleted.current && isLoading)) {
      setIsLoading(false);
    }
  }, [isLoading, show]);

  useEffect(() => {
    const getItFastDimension = findDimensionByLabels(appliedDimensions, [FULFILLMENT_DIMENSIONS_LABELS.GET_IT_FAST]);
    const deliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
    if (getItFastDimension?.refinements.length > 0) {
      setSelectedTile(FULFILLMENT_UI_LABELS.IN_STORE);
    } else if (deliveryDimension?.refinements.length > 0) {
      setSelectedTile(FULFILLMENT_UI_LABELS.DELIVERY);
    } else {
      setSelectedTile(FULFILLMENT_UI_LABELS.BROWSE_ALL);
    }
  }, [appliedDimensions]);

  if (isLoading) {
    return (
      <Skeleton disablePadding grow>
        <SkeletonBlock height={7} />
      </Skeleton>
    );
  }

  return show && (
    <>
      <FulfillmentTypeSelector
        selectedFulfillment={selectedTile}
        handleChange={handleTileChange}
        fulfillmentDimensions={fulfillmentDimensions}
      />
      <SelectedFulfillmentContent
        selectedFulfillment={selectedTile}
        onDimensionChange={handleFulfillmentChange}
        onRefinementChange={handleRefinementChange}
      />
    </>
  );
};

FulfillmentOptions.propTypes = {
  onFulfillmentChange: func.isRequired
};

FulfillmentOptions.displayName = 'FulfillmentOptions';

export { FulfillmentOptions };